import type { Step } from 'react-joyride';

/** How a step advances: 'action' waits for a real user action; 'next' shows a Next button. */
type AdvanceMode = 'action' | 'next';

/** Per-step passthrough data delivered to the tooltip via react-joyride's `data` field. */
export type EngineTourStepData = {
  advance: AdvanceMode;
  /**
   * Id of a safe re-entry step to return to when Back is pressed. Chosen so Back
   * never lands on a transient target (a closed modal, a search result). Absent
   * means the step shows no Back button (e.g. the first step).
   */
  backTo?: string;
  /**
   * Id of an always-present step to rewind to when the tour rehydrates on this
   * step (page reload or Help-menu resume). Set on steps whose target lives
   * inside a dialog/drawer that won't be open after a reload — without it the
   * target never reappears and the tour stalls, then pauses. Must resolve to a
   * strictly-earlier step.
   */
  resumeAnchor?: string;
  phaseNumber?: number;
  phaseTotal?: number;
  phaseLabel?: string;
};

/**
 * Read the engine's per-step data off a react-joyride step. react-joyride types
 * `step.data` loosely, so this is the single place that cast lives — callers get
 * the typed `EngineTourStepData` shape the engine actually writes.
 */
export const getEngineStepData = (step: Step): EngineTourStepData | undefined =>
  step.data as EngineTourStepData | undefined;

/** A single step in a cross-page tour. */
export type EngineTourStep = Step & {
  /** Unique identifier; matches the component's `data-tour="<id>"` and the engine's active-step id. */
  id: string;
  /** Route this step lives on. When set and different from the current route, the engine navigates here. */
  route?: string;
  /**
   * Concrete URL the engine navigates to for this step, preferred over `route`
   * whenever set (and only consulted when `route` is also set). Needed when
   * `route` is a dynamic pattern (e.g. `/recipes/[recipeId]`) that can't be
   * pushed literally — it lets Back return to a page behind a dynamic route.
   */
  href?: string;
  /** Advancement metadata (read by the engine from config and by the tooltip from the Joyride step). */
  data: EngineTourStepData;
};

/** A high-level phase spanning a contiguous range of step indices (inclusive). */
export type HighLevelStep = {
  label: string;
  range: [number, number];
};

/** A complete cross-page tour definition. */
export type EngineTourConfig = {
  tourId: string;
  storageKey: string;
  steps: EngineTourStep[];
  highLevelSteps: HighLevelStep[];
};

/**
 * All engine statuses. Source of truth for both the `TourEngineStatus` type and
 * the runtime validation of persisted store state (see `tour-engine.store.ts`).
 * - `welcome`: a tour has been launched and its welcome modal is shown, before the first step.
 */
export const TOUR_ENGINE_STATUSES = [
  'idle',
  'welcome',
  'running',
  'paused',
  'completed',
  'dismissed'
] as const;

export type TourEngineStatus = (typeof TOUR_ENGINE_STATUSES)[number];
